import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";

/**
 * Admin → Properties: pair one Newsdesk property with its PublishOS
 * property and choose which PublishOS product each newsletter ad
 * position (1-3) bills as. The same product may be chosen for all three.
 *
 * `remote` is the result of /admin/publishos/properties, fetched once by
 * the panel: {configured, properties: [{id, name}], error}.
 */
const SLOTS = [0, 1, 2];
const selectClass =
  "px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] min-w-[180px]";

const sameProducts = (a, b) =>
  SLOTS.every((i) => ((a || [])[i]?.id || "") === ((b || [])[i]?.id || ""));

export default function PublishOSPairing({ property, remote }) {
  const qc = useQueryClient();
  const savedId = property.publishos_property_id || "";
  const [posId, setPosId] = useState(savedId);
  const [products, setProducts] = useState(property.publishos_products || []);
  useEffect(() => {
    setPosId(property.publishos_property_id || "");
    setProducts(property.publishos_products || []);
  }, [property.publishos_property_id, property.publishos_products]);

  const { data: options = [], isFetching, error: productsError } = useQuery({
    queryKey: ["admin", "publishos-products", posId],
    queryFn: () => api.adminPublishosProducts(posId),
    enabled: !!posId && !!remote?.configured,
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: () =>
      api.adminSetPublishosPairing(property.id, {
        publishos_property_id: posId,
        publishos_property_name:
          (remote?.properties || []).find((p) => p.id === posId)?.name ||
          (posId === savedId ? property.publishos_property_name || "" : ""),
        products: SLOTS.map((i) => products[i] || null),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "properties"] });
      qc.invalidateQueries({ queryKey: ["publishos-status"] });
      qc.invalidateQueries({ queryKey: ["newsletter-ads"] });
      toast.success(posId ? tr("PublishOS link saved") : tr("PublishOS link removed"));
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Couldn't save the PublishOS link")),
  });

  if (!remote?.configured) {
    return (
      <div className="text-xs text-[var(--ink-muted)] mt-2">{tr("PublishOS: {error}", { error: remote?.error || tr("not set up on this server") })}
      </div>
    );
  }

  const known = remote.properties || [];
  // Keep the saved choice selectable even if PublishOS couldn't be reached.
  const propertyOptions =
    savedId && !known.some((p) => p.id === savedId)
      ? [{ id: savedId, name: property.publishos_property_name || savedId }, ...known]
      : known;
  const dirty =
    posId !== savedId || !sameProducts(products, property.publishos_products);
  const changingProperty = posId !== savedId && !!savedId;

  const optionsFor = (i) => {
    const chosen = products[i];
    return chosen && !options.some((o) => o.id === chosen.id)
      ? [{ id: chosen.id, name: chosen.name }, ...options]
      : options;
  };

  return (
    <div
      className="mt-2 pt-2 border-t border-[var(--border)]"
      data-testid={`admin-publishos-${property.slug}`}
    >
      <div className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1.5">{tr("PublishOS billing")}</div>
      {remote.error && (
        <div className="text-xs text-red-700 mb-1.5">{remote.error}</div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-[var(--ink-muted)]">{tr("Matching property")}</label>
        <select
          data-testid={`admin-publishos-property-${property.slug}`}
          value={posId}
          onChange={(e) => {
            setPosId(e.target.value);
            // Products belong to a PublishOS property: start clean.
            setProducts(
              e.target.value === savedId ? property.publishos_products || [] : [],
            );
          }}
          className={selectClass}
        >
          <option value="">{tr("Not linked")}</option>
          {propertyOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {isFetching && (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--ink-muted)]" />
        )}
      </div>

      {posId && (
        <div className="mt-2 space-y-1.5">
          {productsError && (
            <div className="text-xs text-red-700">
              {productsError?.response?.data?.detail || tr("Couldn't load PublishOS products")}
            </div>
          )}
          {SLOTS.map((i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-[var(--ink-muted)] w-40">{tr("Newsletter ad position {value}", { value: i + 1 })}
              </label>
              <select
                data-testid={`admin-publishos-product-${property.slug}-${i + 1}`}
                value={products[i]?.id || ""}
                onChange={(e) => {
                  const opt = optionsFor(i).find((o) => o.id === e.target.value);
                  const next = SLOTS.map((k) => products[k] || null);
                  next[i] = opt ? { id: opt.id, name: opt.name } : null;
                  setProducts(next);
                }}
                className={selectClass}
              >
                <option value="">{tr("No product — don't bill this position")}</option>
                {optionsFor(i).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                    {o.rate != null ? ` — ${o.rate}` : ""}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {changingProperty && (
        <div className="text-xs text-amber-700 mt-1.5">{tr("Changing the property unlinks every newsletter advertiser here from its PublishOS client — they will need to be linked again.")}</div>
      )}
      {dirty && (
        <button
          type="button"
          data-testid={`admin-publishos-save-${property.slug}`}
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{tr("Save PublishOS link")}</button>
      )}
    </div>
  );
}
